import { useEffect, useState } from 'react';
import { ArrowUp, BarChart2, Apple, Dumbbell, UtensilsCrossed, Camera, ClipboardCheck, Bot, Pill, History, Flame } from 'lucide-react';

/**
 * Maps DOM section IDs to a label + icon for the sticky indicator.
 * Update this list when adding new sections.
 */
const SECTIONS: Record<string, { label: string; Icon: typeof BarChart2 }> = {
  'dashboard':         { label: 'Dashboard',     Icon: BarChart2 },
  'historial':         { label: 'Historial',     Icon: History },
  'rutina':            { label: 'Rutina',        Icon: Dumbbell },
  'cardio':            { label: 'Cardio',        Icon: Flame },
  'nutrición':         { label: 'Calorías',      Icon: Apple },
  'plan':              { label: 'Plan',          Icon: UtensilsCrossed },
  'progress-photos':   { label: 'Progreso visual', Icon: Camera },
  'evaluación':        { label: 'Evaluación',    Icon: ClipboardCheck },
  'marco':             { label: 'Marco IA',      Icon: Bot },
  'energía':           { label: 'Suplementos',   Icon: Pill },
};

const SECTION_IDS = Object.keys(SECTIONS);

/**
 * Sticky bar that tells the user which section they are currently
 * viewing as they scroll down a long page.
 *
 * Uses IntersectionObserver to detect which section is most visible.
 * Hides itself while still inside Dashboard (the entry section) so it
 * does not visually clutter the very top of the page.
 *
 * Includes a "↑ Top" button that smoothly scrolls back to the top.
 */
export function SectionStickyIndicator() {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  useEffect(() => {
    // Build observer that fires when a section enters/leaves the viewport
    const observer = new IntersectionObserver(
      (entries) => {
        // Track the section with the largest visible intersection ratio.
        // This avoids flicker when two sections are both partially visible.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          const id = visible[0].target.id;
          if (id && SECTIONS[id]) setActiveSection(id);
        }
      },
      {
        // Start "tracking" a section when its top crosses the indicator bar
        // and stop when its bottom crosses it.
        rootMargin: '-100px 0px -50% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    // Observe each known section if it exists in the DOM
    const observed: Element[] = [];
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        observer.observe(el);
        observed.push(el);
      }
    });

    return () => observer.disconnect();
  }, []);

  // Don't show on Dashboard (top of page).
  // Don't show on Rutina either — that section has its own richer sticky
  // bar (day selector + grupo + progreso) that is more useful than just
  // the section name.
  if (!activeSection || activeSection === 'dashboard' || activeSection === 'rutina') {
    return null;
  }

  const meta = SECTIONS[activeSection];
  if (!meta) return null;
  const { label, Icon } = meta;

  return (
    <div
      className="sticky top-14 z-30 bg-background/95 backdrop-blur-sm border-b border-border"
      role="status"
      aria-live="polite"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 text-accent shrink-0" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Estás en
          </span>
          <span className="text-sm font-black uppercase tracking-wider text-foreground truncate">
            {label}
          </span>
        </div>

        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-accent/10 hover:bg-accent/20 text-accent text-[10px] font-bold uppercase tracking-wider transition-colors min-h-[32px] shrink-0"
          aria-label="Volver al inicio"
        >
          <ArrowUp className="w-3.5 h-3.5" />
          Inicio
        </button>
      </div>
    </div>
  );
}
